import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { IoMdHeadset } from 'react-icons/io';
import { useLocation } from 'react-router-dom';
import { resultItem, resultDetails } from './results.module.css';
import ResultUtilityButtons from './resultUtilityButtons/ResultUtilityButtons.jsx';
import highlightText from '../../../utils/highlightText';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { tagHeaderToFilterDictionary } from '../../../utils/resultsSectionSetArray';
import { summaryTagHeadersDict } from '../../../utils/tagHeadersDicts';
import {
  sysReviewFields,
  clinicalTrialFields,
  observationalFields,
  caseReportFields,
  unknownFields,
  microtagFields,
  ignoreEntries,
} from '../../../utils/displayKeys';
// import PositiveOutcome from '../../outcomes/PositiveOutcome';
// import NeutralOutcome from '../../outcomes/NeutralOutcome';
// import NegativeOutcome from '../../outcomes/NegativeOutcome';
import formatLinks from '../../../utils/formatLinks';
// import styles from './results.module.css';

SummarizedResultItem.propTypes = {
  study: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  filteredKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateCallback: PropTypes.func.isRequired,
};

export default function SummarizedResultItem({ study, isOpen, filteredKeys, updateCallback }) {
  // const { docType, studyTypeInt, summaryTitle, year, link } = study;
  const {
    docType,
    studyTypeInt,
    summaryTitle,
    year,
    // outcomeResults,
    link,
    podcastUrl,
  } = study;
  // const displayOutcomes = (outcomeResults => {
  //   const displayOutcomesObj = { positive: '', negative: '', neutral: '' };
  //   if(!outcomeResults) return displayOutcomesObj;
  //   if(outcomeResults.includes('Positive')) displayOutcomesObj.positive = <PositiveOutcome />;
  //   if(outcomeResults.includes('Neutral')) displayOutcomesObj.neutral = <NeutralOutcome />;
  //   if(outcomeResults.includes('Negative')) displayOutcomesObj.negative = <NegativeOutcome />;
  //   return displayOutcomesObj;
  // })(outcomeResults);

  const highlightTagHeader = (tagHeader) => {
    if (!filteredKeys) return;
    if (filteredKeys.includes(tagHeaderToFilterDictionary[tagHeader]))
      return highlightText(`<highlighter>${tagHeader}</highlighter>`, 'highlighter').fragment;
    return tagHeader;
  };

  const studyTableRows = [];
  const populateRows = (field) => {
    if (!field) return;

    const tagHeader = summaryTagHeadersDict[field];
    const entry = study[field];
    if (entry && !ignoreEntries.includes(entry)) {
      //Necessary until all doi values are removed from references
      if (field === 'reference') {
        const rx = /(doi.*[\s]?)/;
        studyTableRows.push(
          <tr key={field}>
            <td>
              <strong>{highlightTagHeader(tagHeader)}</strong>
            </td>
            <td>{highlightText(entry.replace(rx, ''), 'highlighter').fragment}</td>
          </tr>
        );
      } else if (!(field === 'link')) {
        studyTableRows.push(
          <tr key={field}>
            <td>
              <strong>{highlightTagHeader(tagHeader)}</strong>
            </td>
            <td
              dangerouslySetInnerHTML={{
                __html: highlightText(entry.toString().replaceAll('\n', '<br />'), 'highlighter')
                  .raw,
              }}
            ></td>
          </tr>
        );
      } else if (field === 'link') {
        studyTableRows.push(
          <tr key={field}>
            <td>
              <strong>{tagHeader}</strong>
            </td>
            <td>
              {formatLinks(link)
                .split(',')
                .map((link, i) => (
                  <Fragment key={i}>
                    <a href={link.trim()} target="_blank" rel="noopener noreferrer">
                      {link.trim()}
                    </a>
                    <br />
                  </Fragment>
                ))}
            </td>
          </tr>
        );
      }
    }
    // Render podcast player when processing link field, regardless of link value
    if (field === 'link' && podcastUrl && podcastEnabled) {
      studyTableRows.push(
        <tr key="podcastUrl">
          <td style={{ verticalAlign: 'top' }}>
            <strong>{summaryTagHeadersDict['podcastUrl']}</strong>
          </td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <IoMdHeadset style={{ color: '#1a4b8c', fontSize: '24px', flexShrink: 0 }} />
              <audio controls controlsList="nodownload" style={{ width: '100%' }}>
                <source src={podcastUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>
          </td>
        </tr>
      );
    }
  };

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const singleStudyTest = searchParams.get('id');
  const podcastEnabled = useFeatureFlag('podcast');
  const displayStudyType = (fieldArray) => {
    if (singleStudyTest) {
      const newArray = [...fieldArray];
      newArray.splice(1, 0, 'studyType');
      return newArray;
    } else return fieldArray;
  };

  switch (studyTypeInt) {
    case 1:
      if (docType && docType === 'Summary') displayStudyType(sysReviewFields).forEach(populateRows);
      else displayStudyType(microtagFields).forEach(populateRows);
      break;
    case 2:
      if (docType && docType === 'Summary')
        displayStudyType(clinicalTrialFields).forEach(populateRows);
      else displayStudyType(microtagFields).forEach(populateRows);
      break;
    case 3:
      if (docType && docType === 'Summary')
        displayStudyType(observationalFields).forEach(populateRows);
      else displayStudyType(microtagFields).forEach(populateRows);
      break;
    case 4:
      if (docType && docType === 'Summary')
        displayStudyType(caseReportFields).forEach(populateRows);
      else displayStudyType(microtagFields).forEach(populateRows);
      break;
    case 5:
      unknownFields.forEach(populateRows);
      break;
    default:
      break;
  }

  const highlightedTitle = highlightText(summaryTitle, 'highlighter').fragment;
  // const firstLetter = docType.slice(0, 1);
  // const studyTypeBadge = firstLetter !== 'N' ? <span title={firstLetter === 'S' ? 'Full Summary' : 'Microtag'} className={styles.studyTypeBadge}>{docType.slice(0, 1)}</span> : '';
  const studyTypeText =
    docType !== 'None' && docType !== 'Microtag' ? (
      <span style={{ fontWeight: '400' }}>[{docType}]</span>
    ) : (
      ''
    );
  // const { positive, neutral, negative } = displayOutcomes;
  return (
    <>
      <details className={resultItem} open={isOpen} role="tab">
        {
          <summary>
            <strong>
              {highlightedTitle} ({year})
              {/* {positive}
          {neutral}
          {negative}  */}
              {studyTypeText}
            </strong>
          </summary>
        }
        {/* {<summary><strong>{highlightedTitle} ({year}) {studyTypeText}</strong></summary>} */}
        <ResultUtilityButtons study={study} updateCallback={updateCallback} />
        <table className={resultDetails}>
          <tbody>{studyTableRows}</tbody>
        </table>
      </details>
    </>
  );
}
